import { Button, Well, WellControl, WellMessage } from "@don/design-system";
import Hls from "hls.js";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

import { epochFor, mediaOffsetMs, positionOf, timecode, type Timeline } from "./timeline.js";

/**
 * The player: one `<video>` element for the life of the screen, with playlists swapped
 * underneath it.
 *
 * **Why the element is stable and the source is not.** A session carries one playlist per
 * media epoch: hls.js mishandles multiple `EXT-X-MAP` tags in one playlist, and a new
 * epoch means new encoder parameters (docking, rotating, a resolution change mid-session),
 * so the boundary is a real discontinuity (architecture §2.8). Multi-display is the same
 * mechanism with a track index. Both therefore change which playlist is loaded, and
 * neither may change which element it is loaded into: re-mounting the `<video>` drops the
 * decoder, the buffer and the playhead, and the screen the owner was reading blinks back
 * to the beginning. So the element is rendered unconditionally and only the `Hls` instance
 * attached to it is torn down and rebuilt.
 *
 * **What it reports, and why through attributes.** `data-player-state`, `data-track`,
 * `data-epoch`, `data-epoch-size` and `data-playlist` are what a test reads.
 * `data-seeked-to` is cleared whenever the loaded playlist changes, because it is a report
 * about that playlist, and is never cleared within one, for the reason
 * `web/bare-player/index.html` already gives: playback resumes within a frame of a seek, so
 * a test watching a transient state would be racing the media element rather than asserting
 * anything. The screen-switch test depends on both halves: without the clearing it would
 * read the old playlist's value and pass having asserted nothing.
 *
 * **What it shows, and why it is not only an attribute.** A video that is still arriving, a
 * video whose playlist 404s and a video that is playing were one black rectangle, with a
 * `PlayerState` computed and spent entirely on a `data-*` attribute. `CONTEXT.md` says the
 * artifact states must not look alike, so the frame says which one this is (`PlayerStatus`).
 *
 * **It draws the whole well** (issue 200, design-spec §4): head, 16:10 frame, play control,
 * time and the rail it is handed. Playing and paused differ by four things (F1): the control
 * is a filled disc paused and a ring playing, the head says `Paused` or `Playing`, a paused
 * picture carries an overlay, and `onPlaying` lets the side column tone the current line.
 */
export interface SessionPlayerProps {
  timeline: Timeline;
  /**
   * The seek the screen is asking for. A **new `token`** is a new request; the same token
   * with a different position is the player's own report coming back round, which is what
   * stops a `timeupdate` from seeking the element to where it already is.
   */
  seek: { readonly positionMs: number; readonly token: number };
  /** Where the screen believes the playhead is. The transport reads it. */
  positionMs: number;
  onPosition(positionMs: number): void;
  /** The scrubber, laid in the controls row after the play control and the time (variant E). */
  children?: ReactNode;
  /** Left of the well head: the screen name, or the screen picker. */
  screen?: ReactNode;
  /** Right of the well head while there is no picture to be playing or paused. */
  date?: string;
  /** Under the controls: the gap list when there are several. */
  below?: ReactNode;
  /** Told whenever the picture starts or stops playing, so the side column can follow it (F1). */
  onPlaying?(playing: boolean): void;
  /** Load the video again, offered when it did not load at all. */
  onRetry?(): void;
}

type PlayerState = "loading" | "ready" | "playing" | "seeked" | "error" | "unsupported";

/**
 * Which of `wire::ArtifactState`'s four the *video* is in, as this screen can tell.
 *
 * `absent_by_design` is not among them: a session with no video at all never reaches this
 * component, because `SessionScreen` renders the aged-out or the not-connected shape
 * instead. So three, and each is drawn differently.
 */
export type PlayerArtifactState = "present" | "pending" | "failed";

/**
 * Which failure this is, and each of them is read from something the screen measured.
 *
 * It used to be `state === "error" ? "playlist" : "picture"`, which is a guess: a fatal
 * hls.js error is a fatal *anything*, a mux error or a media error or a key error, and in
 * every one of them the surface told the owner the playlist had not loaded while that
 * playlist's duration sat in the transport and its gaps were drawn on the rail beneath.
 * Worse, the sentence could change: any later fatal rewrote a correct explanation into a
 * false one, which the UX review watched happen on a recording whose playlist had parsed.
 *
 * They are read from held facts instead. `parsed` is whether a manifest ever arrived for
 * the loaded playlist; `decoded` is whether a frame ever reached the screen; `holes` is
 * which fragments of this session the loader handled and could not make into buffered
 * media, and of what kind. The first two never un-happen within one playlist; a hole is
 * retracted by one thing only, that fragment's media landing in the buffer. So no seek,
 * play, pause or frame rewrites a correct sentence. `starved` is the one frame-level
 * input, and it only chooses between sentences that all rest on `holes`, so it cannot make
 * the screen claim more than the loader measured.
 */
type PlayerFailure =
  | "playlist"
  | "picture"
  | "stalled"
  | "incomplete"
  | "unplayable"
  | "interrupted"
  | "unsupported";

/**
 * What each failure says, in at most two sentences: what happened, then what is still true
 * (design-spec §6, screen-catalog `session-media-failed`). No sentence names a cause this
 * screen did not measure: `The video didn't load.` is true of a playlist refused and of a
 * playlist whose pictures never came, and says neither, so a later fatal of another kind
 * cannot turn it false. `data-player-failure` keeps the measured kind for whoever reads it.
 *
 * `incomplete` and `unplayable` are the two kinds of hole: a fragment the loader was
 * refused, and a fragment that arrived and would not become media. Both are said over a
 * picture that is still there, so neither may say the video did not load. `stalled` is
 * either of them said from a playhead that has run into one. `interrupted` is what is left
 * when there is no hole: the browser stopped, and this screen does not know why.
 */
const FAILURE_COPY: Record<PlayerFailure, readonly [string, string]> = {
  playlist: ["The video didn't load.", "The transcript and notes are unaffected."],
  picture: ["The video didn't load.", "The transcript and notes are unaffected."],
  stalled: ["The video stopped partway through.", "What comes next didn't reach this page."],
  incomplete: ["Part of the video didn't load.", "What plays here isn't all of it."],
  unplayable: ["Part of the video won't play.", "What plays here isn't all of it."],
  interrupted: ["The video stopped playing.", "The transcript and notes are unaffected."],
  unsupported: ["This browser can't play the video.", "Another browser will play it."],
};

/** What the frame says while the first picture is on its way (screen-catalog `session-media-loading`). */
const LOADING_LINE = "Loading the video";

/**
 * The two ways a fragment can fail to become media: nobody answered, or what answered was
 * not the recording.
 */
type HoleKind = "refused" | "unplayable";

/**
 * The hls.js error details that name a fragment the loader could not use, and which kind
 * of hole each is evidence of.
 *
 * **The evidence was always being delivered.** Every fragment error reaches this component
 * as a non-fatal `Hls.Events.ERROR`, and the handler once read `if (data.fatal)` and
 * dropped the rest; the security review counted six refused loads in one run while the
 * screen denied anything was missing. Round 3 admitted the two refusals and nothing else,
 * and that is the round-4 defect: bytes that arrive with `200` and are not media reach this
 * component as `fragParsingError`, and bytes cut short reach it as the append family, none
 * of which was read. Measured on this fixture with every fragment after the first answered
 * with non-media bytes: `fragParsingError` twice, then hls.js reporting `FRAG_BUFFERED` for
 * the same fragment 130 times in 20 s with nothing in the buffer behind it. So a report
 * that a fragment buffered is not evidence that anything did; `covers` below is.
 *
 * **`bufferStalledError` is deliberately not in it.** It is a report about where the
 * playhead is against the buffer, not about a fragment, and a wholly healthy link produces
 * it: measured with every fragment served 9 s late, five of them in 40 s with zero fragment
 * errors and the buffer growing 2 s at a time. Admitting it latched a failure over that
 * recording at about 9 s, which is the opposite harm. A hole has to be a fragment the
 * loader handled and could not use, and a slow fragment is not that.
 */
const UNUSABLE: ReadonlyMap<string, HoleKind> = new Map<string, HoleKind>([
  [Hls.ErrorDetails.FRAG_LOAD_ERROR, "refused"],
  [Hls.ErrorDetails.FRAG_LOAD_TIMEOUT, "refused"],
  [Hls.ErrorDetails.FRAG_PARSING_ERROR, "unplayable"],
  [Hls.ErrorDetails.FRAG_DECRYPT_ERROR, "unplayable"],
  // The append family. `bufferAppendingError`, the SourceBuffer's own event, names no
  // fragment and is not here; measured on bytes cut short, it is followed every time by
  // one of these two carrying the fragment whose bytes were refused, 174 of 174.
  [Hls.ErrorDetails.BUFFER_APPEND_ERROR, "unplayable"],
  [Hls.ErrorDetails.MEDIA_SOURCE_REQUIRES_RESET, "unplayable"],
  [Hls.ErrorDetails.BUFFER_APPEND_NO_PROGRESS, "unplayable"],
]);

/**
 * The slack hls.js's own fragment tracker allows between a fragment's declared span and
 * the buffered range that holds it (`fragment-tracker.ts`, `bufferPadding`).
 */
const BUFFER_PADDING_S = 0.2;

/** Whether the element holds media for the whole of `[start, end)`, within the padding. */
function covers(buffered: TimeRanges, start: number, end: number): boolean {
  for (let i = 0; i < buffered.length; i += 1) {
    if (buffered.start(i) - BUFFER_PADDING_S <= start && buffered.end(i) + BUFFER_PADDING_S >= end) {
      return true;
    }
  }
  return false;
}

/** One fragment of one playlist. A hole is remembered by this, across playlists. */
function fragmentKey(playlist: string, sn: number | "initSegment"): string {
  return `${playlist}#${sn}`;
}

/**
 * How long a parsed playlist may hold a picture that is not moving before that stops being
 * "pending".
 *
 * A fatal hls.js error is the loud failure. The quiet one is a playlist that parses while
 * its fragments do not arrive, which is what a signed-URL expiry, a reconciliation hole or
 * a lifecycle deletion that has outrun `videoExpiresAtMs` look like from the browser:
 * hls.js retries rather than escalating, and measured twice on this commit's fixture there
 * is no fatal error in the first twenty-two seconds, which is already longer than an owner
 * waits. So the screen sat at `ready` over a black rectangle with a clock running under it.
 * Measured by the UX review at 155 ms to `ready` with every fragment aborted, so the
 * picture, not the manifest, is what decides this one.
 *
 * This watchdog answers one question only: did a frame ever arrive. The recording that dies
 * **partway through** is a different question and is not its job; see
 * `UNUSABLE_IS_A_HOLE_AFTER_MS`. Re-arming this one on a stall was the repair that put
 * a frame-level fact in charge of a recording-level claim, and every way those two got
 * confused afterwards reached the owner as a false sentence.
 */
const BLANK_PICTURE_IS_A_FAILURE_AFTER_MS = 6000;

/**
 * How long a fragment the loader could not use may stand, with its media still not in the
 * buffer, before it is a hole in the recording.
 *
 * **A budget, because one failure on its own is not evidence.** hls.js retries a fragment
 * with a growing backoff, so one refusal is what a flaky link looks like as well as what a
 * hole looks like, and one unplayable answer is what a proxy interstitial looks like as
 * well as what a truncated object looks like. The two are told apart by what follows: that
 * fragment's media landing in the buffer closes the budget, and nothing landing inside it
 * is the hole. The budget is per fragment, so a fragment that buffers cannot pay for a
 * different one that did not.
 *
 * **The budget is also the only clock in this, and it starts on a loader report, never on
 * the picture.** A picture that has not moved is what a slow link looks like too: measured
 * with every fragment served 9 s late, the playhead sits still for stretches longer than
 * this budget on a recording that is whole. So the picture arms nothing here; see
 * `starved`.
 */
const UNUSABLE_IS_A_HOLE_AFTER_MS = 6000;

export function SessionPlayer({
  timeline,
  seek,
  positionMs,
  onPosition,
  children,
  screen,
  date,
  below,
  onPlaying,
  onRetry,
}: SessionPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [ordinal, setOrdinal] = useState(() => epochFor(timeline, seek.positionMs).ordinal);
  const [state, setState] = useState<PlayerState>("loading");
  const [seekedTo, setSeekedTo] = useState<number | null>(null);
  const [paused, setPaused] = useState(true);
  // Whether this playlist has put a frame on the screen. `ready` means a manifest parsed,
  // which says nothing about bytes; a decoded frame is the only evidence the video is here.
  const [decoded, setDecoded] = useState(false);
  // Whether a manifest ever arrived for the playlist that is loaded. Held rather than read
  // off `state`, because `state` moves on and this is what tells "the playlist did not
  // load" apart from "it loaded and the bytes behind it did not".
  const [parsed, setParsed] = useState(false);
  // **What the picture is doing right now, and it is allowed to come and go.** The element
  // asked for the next frame and did not have it. This drives the wording and the shape of
  // the surround, and nothing else: a frozen picture is a fact about the playhead, and
  // whether the recording is whole is not.
  const [starved, setStarved] = useState(false);
  // No frame has ever arrived for this playlist, decided by the watchdog rather than by
  // the manifest. Cleared by a frame, because a slow playlist that finally paints is a
  // slow link and not a recording that is gone.
  const [blank, setBlank] = useState(false);
  // **The recording-level fact: which fragments of this session are holes.** Keyed by
  // playlist and fragment, so it is held for the session rather than for the loaded
  // playlist: a hole in epoch 0 is still a hole in the recording while the playhead is in
  // epoch 1, and `data-artifact-state` is about the session. It is never reset; the
  // player is keyed by session in `SessionScreen`, so a new session is a new player.
  // A hole is opened by the budget below and closed by one thing only, that fragment's
  // media landing in the buffer after a complete-length response for it. No seek, no
  // play, no pause and no decoded frame touches it. Nothing under the rail reads this:
  // the legend is a timeline fact, and this set reaches the owner only as the surround.
  const [holes, setHoles] = useState<ReadonlyMap<string, HoleKind>>(() => new Map());
  // Fragments the loader could not use whose budget is still running. A ref, because a
  // running budget is not a fact the screen shows. A budget outlives the loader that
  // opened it, since it is about a fragment of this session and not of the loaded
  // playlist (the loader's cleanup says why), and dies on unmount only, just below.
  const pendingHoles = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  // Fragments whose most recent response was the whole of the byte range the playlist
  // names. `covers` trusts the manifest's DECLARED span, so a hole can only be made good
  // once a complete-length response for that fragment has been seen. Both sides of that
  // comparison come from the stream, the range the playlist declared and the length the
  // object answered, so it is evidence against a truncating transport and none against a
  // rewritten playlist, which can declare a range as short as its `#EXTINF`; and a
  // fragment with no `#EXT-X-BYTERANGE` has nothing to compare and is taken as whole.
  // Measured (2026-09-19): a healthy fragment's `payload.byteLength` equals its byte
  // range exactly; non-media bytes are 18; a fragment cut in half is 17129 of 34258.
  const complete = useRef(new Set<string>());
  // Where the element should land once the playlist it needs has parsed. A ref, not state:
  // the `Hls` effect reads it at the moment the manifest arrives, and a re-render between
  // those two moments would restart the load it is waiting on.
  const pending = useRef<number | null>(seek.positionMs);
  // Which playlist is attached to the element right now, or `null` while none is. A seek
  // inside the loaded playlist is a `currentTime` write; a seek outside it is a reload with
  // somewhere to land. Without this the two cases are indistinguishable and the second one
  // silently swallows the first: a seek within one epoch changes nothing the loader depends
  // on, so the loader never reruns and nothing ever applies the request.
  const loaded = useRef<string | null>(null);

  // Budgets outlive the loader that opened them (see the loader's cleanup) and die with
  // the player, which `SessionScreen` keys by session.
  useEffect(
    () => () => {
      for (const timer of pendingHoles.current.values()) clearTimeout(timer);
      pendingHoles.current.clear();
    },
    [],
  );

  // Derived, not held. Switching screens hands this component a different track's timeline
  // with the same epoch ordinals on it, so holding the ordinal and looking the epoch up
  // makes the switch a change of playlist and nothing else.
  const epoch = timeline.epochs.find((each) => each.ordinal === ordinal) ?? timeline.epochs[0]!;

  // A new seek request: choose the epoch that holds it, and remember where to land. Runs
  // before the loader below, so the loader always sees the target that provoked it.
  useEffect(() => {
    const target = epochFor(timeline, seek.positionMs);
    const video = videoRef.current;
    if (video && loaded.current === target.playlist) {
      video.currentTime = mediaOffsetMs(timeline, target, seek.positionMs) / 1000;
      return;
    }
    pending.current = seek.positionMs;
    setOrdinal(target.ordinal);
    // **`seek.token` alone, deliberately.** `seek.positionMs` is read at the top of this
    // effect rather than depended on, because the screen writes it back from the player's
    // own `timeupdate`: depending on it would make every frame of playback a fresh seek to
    // where the element already is. There is no linter in this repo enforcing exhaustive
    // dependency lists, so this paragraph is the whole of the guard. The property is
    // asserted instead by the e2e, where a seek that re-fired every frame could not hold a
    // position across a screen switch.
  }, [seek.token]);

  // One `Hls` per playlist, attached to the one element.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    setState("loading");
    // `data-seeked-to` is a report about the playlist that is loaded, so it is cleared when
    // that changes and set again when the new one has landed. Within one playlist it is
    // never cleared, for the reason `web/bare-player/index.html` gives: playback resumes
    // within a frame of a seek and a transient state is a thing a test races.
    setSeekedTo(null);
    // A new playlist has shown no frame yet, whatever the last one managed.
    setDecoded(false);
    setParsed(false);
    setStarved(false);
    setBlank(false);
    // `holes` is deliberately not reset here. A different playlist is a different set of
    // bytes, and the holes in the last one are still holes in this recording.

    // A fragment the loader could not use. Opens its budget unless one is already running
    // or it is already a hole; the budget promotes it, and only `madeGood` closes either.
    const failedToUse = (key: string, kind: HoleKind) => {
      if (pendingHoles.current.has(key)) return;
      const timer = setTimeout(() => {
        pendingHoles.current.delete(key);
        setHoles((held) => (held.has(key) ? held : new Map(held).set(key, kind)));
      }, UNUSABLE_IS_A_HOLE_AFTER_MS);
      pendingHoles.current.set(key, timer);
    };
    // That fragment's media is in the buffer. The one retraction there is.
    const madeGood = (key: string) => {
      const timer = pendingHoles.current.get(key);
      if (timer !== undefined) {
        clearTimeout(timer);
        pendingHoles.current.delete(key);
      }
      setHoles((held) => {
        if (!held.has(key)) return held;
        const next = new Map(held);
        next.delete(key);
        return next;
      });
    };

    const land = () => {
      const target = pending.current;
      pending.current = null;
      loaded.current = epoch.playlist;
      setParsed(true);
      setState("ready");
      if (target === null) return;
      video.currentTime = mediaOffsetMs(timeline, epoch, target) / 1000;
    };

    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: false });
      hls.on(Hls.Events.MANIFEST_PARSED, land);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        // The loader handled a fragment and could not use it. Fatal or not: hls.js spends
        // most of a dead recording below fatal, retrying, and this is the only report of
        // that anything makes. Dropping it is what let the screen print a completeness
        // claim over eight refused requests, and reading only the refusals is what let it
        // print one over bytes that arrived and would not play.
        const kind = UNUSABLE.get(data.details);
        if (kind !== undefined && data.frag) {
          failedToUse(fragmentKey(epoch.playlist, data.frag.sn), kind);
        }
        // Only a fatal error is a broken player. hls.js recovers from the rest by itself,
        // and painting a recovered stall as a failure would teach the owner to distrust a
        // recording that is fine.
        if (data.fatal) setState("error");
      });
      // The loader says the fragment buffered. It says so over an empty append too,
      // measured 130 times in 20 s on bytes that were not media, and over the first half
      // of a fragment cut short, so the element is asked whether the fragment's span is in
      // its buffer. If it is, that fragment is made good; if it is not, the bytes arrived
      // and did not become that fragment's media, which is a hole of the second kind. The
      // init segment has no span, and buffering it is what it is for.
      // The bytes that answered, measured against the bytes the playlist asked for. A
      // short answer is a fulfilled request and is not that fragment, whatever decodes.
      hls.on(Hls.Events.FRAG_LOADED, (_event, data) => {
        const key = fragmentKey(epoch.playlist, data.frag.sn);
        const [from, to] = data.frag.byteRange;
        const whole = from === undefined || to === undefined || data.payload.byteLength === to - from;
        if (whole) complete.current.add(key);
        else {
          complete.current.delete(key);
          failedToUse(key, "unplayable");
        }
      });
      hls.on(Hls.Events.FRAG_BUFFERED, (_event, data) => {
        const key = fragmentKey(epoch.playlist, data.frag.sn);
        if (data.frag.sn === "initSegment" || (complete.current.has(key) && covers(video.buffered, data.frag.start, data.frag.end))) {
          madeGood(key);
        } else {
          failedToUse(key, "unplayable");
        }
      });
      hls.loadSource(epoch.playlist);
      hls.attachMedia(video);
      return () => {
        loaded.current = null;
        // **A running budget is NOT cancelled here.** It used to be, on the ground that a
        // loader that no longer exists can neither make the fragment good nor fail to.
        // Measured, that let a scrub or a screen switch inside the 6 s budget erase the
        // evidence and reprint the denial on 47 of 48 samples, and `onEnded` did the same
        // at every epoch boundary with no owner action. The budget is about a fragment of
        // this SESSION, so it runs to its end whatever the loader does; if it elapses, the
        // fragment is a hole until a later loader lands its bytes. Timers are cleared on
        // unmount only, below.
        hls.destroy();
      };
    }

    // Safari plays HLS natively and hls.js defers to it.
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = epoch.playlist;
      video.addEventListener("loadedmetadata", land);
      return () => {
        loaded.current = null;
        video.removeEventListener("loadedmetadata", land);
      };
    }

    // Neither path exists here: this browser cannot play HLS at all. Its own spelling,
    // because "the playlist did not load" would be a cause this screen never measured.
    setState("unsupported");
    return undefined;
  }, [epoch, timeline]);

  // What the element is doing, translated back into timeline time.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const report = () => onPosition(positionOf(timeline, epoch, video.currentTime * 1000));
    const onSeeked = () => {
      setSeekedTo(video.currentTime);
      setState("seeked");
      // The position changed, so an earlier report of being starved was about a position
      // the owner has left. If the new one has no bytes either, `waiting` says so again.
      // **This clears a frame-level fact only.** Clearing the recording-level one here is
      // what printed the denial again over a recording already proved to be missing
      // twenty-two of its twenty-four seconds, for the eight seconds the buffered stretch
      // replayed.
      setStarved(false);
      report();
    };
    const onDecoded = () => {
      setDecoded(true);
      setBlank(false);
    };
    // The element wanted the next frame and did not have it. Not a failure on its own: a
    // slow link stalls too, and this says nothing about whether the bytes exist. It picks
    // the wording once the loader has already established that some of them do not.
    const onWaiting = () => setStarved(true);
    const onPlaying = () => {
      setState("playing");
      setPaused(false);
      // A moving picture is not an absent one, so the wording stops blaming the picture and
      // the surround stops covering it. What the loader was refused is untouched.
      setStarved(false);
      setBlank(false);
    };
    const onPause = () => setPaused(true);
    const onEnded = () => {
      // The media ran out because it was supposed to. Some browsers report that as a wait
      // for data first, and an epoch playing to its end is the opposite of a failure.
      setStarved(false);
      // The epoch ran out. If another follows it, that is the next stretch of the same
      // session and the player continues into it rather than stopping mid-recording.
      const next = timeline.epochs.find(
        (each) => each.startMs >= epoch.startMs + epoch.durationMs,
      );
      if (!next) return;
      pending.current = next.startMs;
      setOrdinal(next.ordinal);
    };
    video.addEventListener("timeupdate", report);
    video.addEventListener("seeked", onSeeked);
    video.addEventListener("loadeddata", onDecoded);
    video.addEventListener("waiting", onWaiting);
    video.addEventListener("playing", onPlaying);
    video.addEventListener("pause", onPause);
    video.addEventListener("ended", onEnded);
    return () => {
      video.removeEventListener("timeupdate", report);
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("loadeddata", onDecoded);
      video.removeEventListener("waiting", onWaiting);
      video.removeEventListener("playing", onPlaying);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ended", onEnded);
    };
  }, [epoch, timeline, onPosition]);

  // **Did a frame ever arrive.** A playlist that parsed over fragments that did not come.
  // Watched on the picture rather than on hls.js's error stream, because that stream may
  // stay silent: a playlist with no usable level produces no refusal at all, and this is
  // the net under that. It answers this one question and stops, which is why a frame
  // clears it.
  useEffect(() => {
    if (state === "error" || state === "unsupported") return undefined;
    if (decoded) return undefined;
    const timer = setTimeout(() => {
      const video = videoRef.current;
      // `HAVE_CURRENT_DATA`: a frame the element has not told us about yet is still a
      // frame, and a slow link that got there is not a recording that is gone.
      if (video && video.readyState >= 2) {
        setDecoded(true);
        return;
      }
      setBlank(true);
    }, BLANK_PICTURE_IS_A_FAILURE_AFTER_MS);
    return () => clearTimeout(timer);
  }, [decoded, state, epoch]);

  // **Is every fragment the loader handled in the buffer.** A different question from the
  // one above, asked of the loader rather than of the picture, and the answer is a
  // property of the recording rather than of the playhead. It is answered in the loader
  // effect, fragment by fragment, and held in `holes` for the session.
  //
  // It is deliberately not armed by playback, and deliberately not cleared by it. hls.js
  // buffers ahead whether or not anybody presses play, so a paused screen is refused the
  // same bytes a playing one is, and the owner who never clicks is exactly the owner this
  // sentence is for.
  const incomplete = holes.size > 0;

  // `present` is a claim about every fragment this session's loader handled, and no more
  // than that, since the loader sees a window of a recording and not the recording. One
  // hole is enough to deny it on its own, whatever the picture happens to be doing.
  const artifact: PlayerArtifactState =
    state === "error" || state === "unsupported" || blank || incomplete
      ? "failed"
      : decoded
        ? "present"
        : "pending";

  // Read, not guessed. Every arm that says part of the recording is missing rests on
  // `holes`, which is fragments the loader handled and could not make into media, each
  // with its kind. When holes of both kinds exist the refusal is named, which leaves the
  // unplayable part unsaid rather than saying anything false. `starved` only chooses the
  // stalled wording over the kind's, so the worst a moving picture can do is change which
  // of two true sentences is on the screen.
  const failure: PlayerFailure =
    state === "unsupported"
      ? "unsupported"
      : !parsed
        ? "playlist"
        : !decoded
          ? "picture"
          : incomplete
            ? starved
              ? "stalled"
              : [...holes.values()].includes("refused")
                ? "incomplete"
                : "unplayable"
            : "interrupted";

  // **Over the picture when there is no picture, a band over it when there is.** A recording
  // that is incomplete stays said, because that is true of the whole of it, and washing out
  // media the owner can see to say it was the other half of an earlier defect. What is
  // covered is decided by the picture; what is said is decided by the loader.
  const obscures = artifact !== "present" && (!decoded || starved);

  // F1: the state word, the button fill and the overlay all follow the element's own
  // `playing` and `pause` events, and the side column is told so it can tone its line. A
  // player that goes (a stills or refused screen, `Try again`) plays nothing, so it says so
  // on the way out; the side column otherwise kept following a video no longer on the page.
  useEffect(() => {
    onPlaying?.(!paused);
    return () => onPlaying?.(false);
  }, [paused, onPlaying]);

  const toggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) void video.play();
    else video.pause();
  };

  return (
    <Well
      head={screen}
      // Playing or paused is a fact about a picture, so it is said once there is one; until
      // then the head carries the date (variant E's loading and failed wells).
      headEnd={
        decoded ? (
          <span data-well-state="" style={{ color: "var(--well-ink)" }}>
            {paused ? "Paused" : "Playing"}
          </span>
        ) : (
          date
        )
      }
      overlay={decoded && paused && !obscures}
      frame={
        <div
          data-player-surface
          data-artifact-state={artifact}
          style={{ position: "absolute", inset: 0, display: "flex" }}
        >
          <video
            ref={videoRef}
            data-player
            data-player-state={state}
            data-track={timeline.trackIndex}
            data-epoch={epoch.ordinal}
            // What changed at the boundary, which on issue 08's mid-session resolution change
            // is the whole of what changed. Rendered rather than held so the swap is readable
            // and assertable instead of being a field nothing reads.
            data-epoch-size={`${epoch.width}x${epoch.height}`}
            data-playlist={epoch.playlist}
            data-seeked-to={seekedTo === null ? undefined : seekedTo.toFixed(2)}
            playsInline
            muted
            preload="metadata"
            // A click on the picture plays or pauses it, as the play control does. A mouse
            // convenience only: the control stays the keyboard's way in, so the element takes
            // no tab stop. The paused overlay ignores pointer events, so it does not catch it.
            onClick={decoded ? toggle : undefined}
            // Flat, and transparent: the well behind it is the one dark area, and a gradient
            // or a fill of its own would compete with the recorded pixels the owner reads.
            style={{
              width: "100%", height: "100%", objectFit: "contain", display: "block",
              cursor: decoded ? "pointer" : undefined,
            }}
          />
          {artifact !== "present" && (
            <PlayerStatus artifact={artifact} why={failure} obscures={obscures} onRetry={onRetry} />
          )}
        </div>
      }
      controls={
        <>
          <WellControl kind={paused ? "play" : "pause"} disabled={!decoded} onClick={toggle} />
          {/*
            **The element's own controls are off, and the clock here is the reason.** A native
            control bar counts the media it has loaded, which on a session with gaps is shorter
            than the session. This one counts the timeline, which is what the rail beside it
            draws and what every timecode in the side column refers to. With no picture yet
            there is no position to report, so it reads `–:–` (screen-catalog
            `session-media-loading`).
          */}
          <span
            data-transport-readout
            data-elapsed=""
            style={WELL_TIME}
          >
            {`${decoded ? timecode(positionMs) : "–:–"} / ${timecode(timeline.durationMs)}`}
          </span>
          {children}
        </>
      }
      below={below}
    />
  );
}

/** The time beside the well's controls (variant E `.ve-time`), shared with the stills well. */
export const WELL_TIME: CSSProperties = {
  paddingTop: 7, minWidth: 96, whiteSpace: "nowrap", fontSize: "var(--type-small-size)",
  color: "var(--well-ink)", fontVariantNumeric: "tabular-nums",
};

/**
 * Which artifact state the picture is in, said in the frame.
 *
 * **Why it is drawn and not only reported.** `CONTEXT.md`: the four artifact states must
 * not look alike. A video still arriving is the pending ring and `Loading the video`; a
 * video that did not load is the `--problem-on-well` triangle, the sentence and `Try again`
 * over the whole frame; part of a recording missing under a picture that plays is a band
 * across the top of the frame; present is nothing at all, because a mark saying the
 * ordinary thing happened is noise over the pixels the owner came to read.
 *
 * `why` is read from something this screen measured rather than inferred from the shape of
 * the last event (see `PlayerFailure`), and stays on `data-player-failure`.
 *
 * `obscures` is the picture's say in this and the whole of it: nothing to see behind the
 * words when no frame has arrived or the one on screen has stopped.
 */
function PlayerStatus({
  artifact,
  why,
  obscures,
  onRetry,
}: {
  artifact: PlayerArtifactState;
  why: PlayerFailure;
  obscures: boolean;
  onRetry?: () => void;
}) {
  const hooks = {
    "data-player-status": artifact,
    "data-player-failure": artifact === "pending" ? undefined : why,
    "data-player-status-obscures": obscures ? "" : undefined,
  };
  if (artifact === "pending") return <WellMessage kind="loading" title={LOADING_LINE} {...hooks} />;
  const [line, then] = FAILURE_COPY[why];
  if (!obscures) return <WellMessage kind="partial" title={line} body={then} {...hooks} />;
  return (
    <WellMessage
      kind="failed"
      title={line}
      body={then}
      action={
        // A retry remounts the same player in the same browser, which cannot play HLS any
        // better the second time, so `unsupported` offers none.
        onRetry !== undefined && why !== "unsupported" ? (
          <Button variant="secondary" size="sm" data-player-retry="" onClick={onRetry}>
            Try again
          </Button>
        ) : undefined
      }
      {...hooks}
    />
  );
}
